import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
// @mui
import { Box, Drawer } from '@mui/material';
// hooks
import useResponsive from 'hooks/useResponsive';
// components
import Logo from 'components/logo';
import Scrollbar from 'components/scrollbar';
import NavSection from 'components/nav-section';
//
import config from 'config';
import { useStateSelector } from '../../../context/StateSelectors';
import { cloneDeep } from 'lodash';
import AccountPopover from '../../../popovers/AccountPopover';

// ----------------------------------------------------------------------

const NAV_WIDTH = 280;

// ----------------------------------------------------------------------

Nav.propTypes = {
    openNav: PropTypes.bool,
    onCloseNav: PropTypes.func
};

export default function Nav({ openNav, onCloseNav }) {
    const permissions = useStateSelector((state) => state.permissions);
    const currentRole = useStateSelector(
        (state) => state.userInfo.role?.systemRole
    );
    const { pathname } = useLocation();

    const isDesktop = useResponsive('up', 'lg');

    useEffect(() => {
        if (openNav) {
            onCloseNav();
        }
    }, [pathname]);

    const hasReadPermission = (permissionKey) => {
        return (
            permissions[permissionKey] &&
            permissions[permissionKey].read !== 'deny'
        );
    };

    const hasRolePermission = (roles) => {
        return roles.includes(currentRole);
    };

    const filterMenuItems = (menu) => {
        const navMenu = cloneDeep(menu);
        if (!permissions || !Object.keys(permissions).length) return [];

        return navMenu.filter((menuItem) => {
            if (!menuItem.controller && !menuItem.sublistData) return true;
            if (menuItem.sublistData) {
                menuItem.sublistData = filterMenuItems(menuItem.sublistData);
                return menuItem.sublistData.length > 0;
            }
            return hasReadPermission(menuItem.controller);
        });
    };

    const navConfig = config.menu
        .filter((sec) => hasRolePermission(sec.roles))
        .map((section) => ({
            ...section,
            items: filterMenuItems(section.items)
        }));

    const renderContent = (
        <Scrollbar
            sx={{
                "height": 1,
                '& .simplebar-content': {
                    height: 1,
                    display: 'flex',
                    flexDirection: 'column'
                }
            }}
        >
            <Box sx={{ display: 'inline-flex' }}>
                <Logo />
            </Box>

            <NavSection
                data={navConfig.map((navEl) => {
                    if (navEl.sublistData && navEl.sublistData.length === 1) {
                        return {
                            ...navEl.sublistData[0],
                            titleKey: navEl.titleKey,
                            icon: navEl.icon
                        };
                    }
                    return navEl;
                })}
            />

            <Box sx={{ flexGrow: 1 }} />

            <AccountPopover />
        </Scrollbar>
    );

    return (
        <Box
            component="nav"
            sx={{
                flexShrink: { lg: 0 },
                width: { lg: NAV_WIDTH }
            }}
        >
            {isDesktop ? (
                <Drawer
                    open
                    variant="permanent"
                    PaperProps={{
                        sx: {
                            width: NAV_WIDTH,
                            bgcolor: 'background.neutral',
                            borderRightStyle: 'solid',
                            borderColor: 'borderGrey'
                        }
                    }}
                >
                    {renderContent}
                </Drawer>
            ) : (
                <Drawer
                    open={openNav}
                    onClose={onCloseNav}
                    ModalProps={{
                        keepMounted: true
                    }}
                    PaperProps={{
                        sx: { width: NAV_WIDTH }
                    }}
                >
                    {renderContent}
                </Drawer>
            )}
        </Box>
    );
}
